

import { useState } from 'react';
import { Loader2, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiFetch } from '@/lib/api';
import type { Profile } from '@/lib/db/schema';

const AVATARS = [
  { id: 'warrior', label: 'Warrior', emoji: '⚔️' },
  { id: 'mage', label: 'Mage', emoji: '🔮' },
  { id: 'rogue', label: 'Rogue', emoji: '🗡️' },
  { id: 'ranger', label: 'Ranger', emoji: '🏹' },
  { id: 'cleric', label: 'Cleric', emoji: '✨' },
  { id: 'bard', label: 'Bard', emoji: '🎭' },
];

interface ProfileFormProps {
  profile: Profile;
}

export function ProfileForm({ profile }: ProfileFormProps) {
  const [displayName, setDisplayName] = useState(profile.displayName);
  const [avatar, setAvatar] = useState(profile.avatar || 'warrior');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(null);

    try {
      const response = await apiFetch('/api/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ displayName, avatar }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update profile');
      }

      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Something went wrong',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Display Name */}
      <div>
        <label htmlFor="displayName" className="block text-sm text-neutral-400 mb-2">
          Display Name
        </label>
        <input
          id="displayName"
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          required
          minLength={2}
          maxLength={50}
          className="w-full bg-black border border-neutral-800 rounded-lg px-3 py-2 text-white placeholder-neutral-600 focus:outline-none focus:border-neutral-700"
        />
      </div>

      {/* Avatar Selection */}
      <div>
        <label className="block text-sm text-neutral-400 mb-2">Avatar</label>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {AVATARS.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => setAvatar(a.id)}
              className={cn(
                'flex flex-col items-center gap-1 p-3 rounded-lg border transition-colors',
                avatar === a.id
                  ? 'border-white bg-neutral-800'
                  : 'border-neutral-800 hover:border-neutral-700'
              )}
            >
              <span className="text-2xl">{a.emoji}</span>
              <span className="text-xs text-neutral-400">{a.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Message */}
      {message && (
        <div
          className={cn(
            'px-4 py-3 rounded-lg text-sm',
            message.type === 'success'
              ? 'bg-green-500/10 text-green-400 border border-green-500/30'
              : 'bg-red-500/10 text-red-400 border border-red-500/30'
          )}
        >
          {message.text}
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isLoading}
        className={cn(
          'w-full py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2',
          isLoading
            ? 'bg-neutral-800 text-neutral-500 cursor-not-allowed'
            : 'bg-white text-black hover:bg-neutral-200'
        )}
      >
        {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Saving...
          </>
        ) : (
          <>
            <Check className="w-4 h-4" />
            Save Changes
          </>
        )}
      </button>
    </form>
  );
}
